/**
 * Needs-input contract (ENG-336, epic ENG-E63 "Waiting for your reply").
 *
 * When a non-interactive build needs something only the owner can provide
 * (a cloud resource id, a secret stored somewhere, a decision), the
 * orchestrator ends its run with ONE final line:
 *
 *   COMPBOX_NEEDS_INPUT {"question":"...","options":[...],...}
 *
 * The runner parses that line with {@link parseNeedsInputMarker}, pauses the
 * build, and shows the owner the request ({@link formatNeedsInputText}). The
 * owner's reply is screened with {@link looksLikeSecret} so secret values are
 * never pasted into a chat turn.
 *
 * This is distinct from `COMPBOX_NEEDS_HUMAN_REVIEW`, which is reserved for
 * hard gates (plan compliance, QA, security, architect).
 *
 * Pure module: no node: imports, no dependencies. Used by api, web (browser)
 * and the bundled CLI.
 */
/** The final-line marker token. */
export const NEEDS_INPUT_MARKER = "COMPBOX_NEEDS_INPUT";
/** Maximum accepted size of the marker's JSON payload, in UTF-8 bytes (32 KB). */
export const NEEDS_INPUT_MAX_JSON_BYTES = 32 * 1024;
/** True for a known {@link NeedsInputKind}. */
export function isNeedsInputKind(value) {
    return value === "merge_approval" || value === "cloud_preflight" || value === "merge_prs";
}
function isStringArray(value) {
    return Array.isArray(value) && value.every((v) => typeof v === "string");
}
function isPlainObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}
function isOption(value) {
    if (!isPlainObject(value))
        return false;
    if (typeof value.label !== "string" || !isStringArray(value.steps))
        return false;
    return value.onMachine === undefined || typeof value.onMachine === "boolean";
}
function isOwnerItem(value) {
    return isPlainObject(value) && typeof value.title === "string" && typeof value.how === "string";
}
/** Type guard for {@link NeedsInputRequest}. Unknown extra fields are tolerated. */
export function isNeedsInputRequest(value) {
    if (!isPlainObject(value))
        return false;
    if (typeof value.question !== "string" || value.question.trim() === "")
        return false;
    if (value.why !== undefined && typeof value.why !== "string")
        return false;
    if (value.steps !== undefined && !isStringArray(value.steps))
        return false;
    if (value.options !== undefined && !(Array.isArray(value.options) && value.options.every(isOption))) {
        return false;
    }
    if (value.afterAnswer !== undefined && typeof value.afterAnswer !== "string")
        return false;
    if (value.ownerItems !== undefined &&
        !(Array.isArray(value.ownerItems) && value.ownerItems.every(isOwnerItem))) {
        return false;
    }
    if (value.ownerTaskIds !== undefined && !isStringArray(value.ownerTaskIds))
        return false;
    if (value.kind !== undefined && !isNeedsInputKind(value.kind))
        return false;
    if (value.prNumber !== undefined &&
        !(typeof value.prNumber === "number" && Number.isInteger(value.prNumber) && value.prNumber > 0)) {
        return false;
    }
    if (value.prNumbers !== undefined &&
        !(Array.isArray(value.prNumbers) &&
            value.prNumbers.length <= 50 &&
            value.prNumbers.every((n) => typeof n === "number" && Number.isInteger(n) && n > 0))) {
        return false;
    }
    return true;
}
/** UTF-8 byte length without TextEncoder/Buffer (keeps the module environment-neutral). */
function utf8ByteLength(text) {
    let bytes = 0;
    for (let i = 0; i < text.length; i++) {
        const code = text.charCodeAt(i);
        if (code < 0x80)
            bytes += 1;
        else if (code < 0x800)
            bytes += 2;
        else if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
            bytes += 4;
            i++;
        }
        else
            bytes += 3;
    }
    return bytes;
}
/** Copy only the known fields, so stray extra keys never travel further. */
function normalize(req) {
    const out = { question: req.question };
    if (req.why !== undefined)
        out.why = req.why;
    if (req.steps !== undefined)
        out.steps = [...req.steps];
    if (req.options !== undefined) {
        out.options = req.options.map((o) => {
            const option = { label: o.label, steps: [...o.steps] };
            if (o.onMachine !== undefined)
                option.onMachine = o.onMachine;
            return option;
        });
    }
    if (req.afterAnswer !== undefined)
        out.afterAnswer = req.afterAnswer;
    if (req.ownerItems !== undefined) {
        out.ownerItems = req.ownerItems.map((i) => ({ title: i.title, how: i.how }));
    }
    if (req.ownerTaskIds !== undefined)
        out.ownerTaskIds = [...req.ownerTaskIds];
    if (req.kind !== undefined)
        out.kind = req.kind;
    if (req.prNumber !== undefined)
        out.prNumber = req.prNumber;
    if (req.prNumbers !== undefined)
        out.prNumbers = [...req.prNumbers];
    return out;
}
/**
 * A request as an agent may emit it: the runner-only `kind` / `prNumber` are
 * dropped, so an agent can never pose as the merge-approval (or preflight)
 * question (ENG-E64).
 */
export function stripRunnerOnlyFields(req) {
    const { kind: _kind, prNumber: _prNumber, prNumbers: _prNumbers, ...rest } = req;
    return rest;
}
/**
 * Parse a `COMPBOX_NEEDS_INPUT {json}` marker from a run's output.
 *
 * The marker is recognised ONLY when it starts the last non-empty line of the
 * output (leading spaces/tabs allowed), followed by whitespace and a single
 * JSON object on the same line. A heading (`## COMPBOX_NEEDS_INPUT`), a quoted
 * or backticked mention, or a marker followed by more output never matches.
 *
 * Returns `null` when there is no marker, the JSON is invalid or larger than
 * {@link NEEDS_INPUT_MAX_JSON_BYTES}, or the payload fails
 * {@link isNeedsInputRequest}. Never throws.
 */
export function parseNeedsInputMarker(output) {
    if (typeof output !== "string" || output === "")
        return null;
    const lines = output.split(/\r?\n/);
    let last;
    for (let i = lines.length - 1; i >= 0; i--) {
        const line = lines[i];
        if (line !== undefined && line.trim() !== "") {
            last = line;
            break;
        }
    }
    if (last === undefined)
        return null;
    const line = last.replace(/^[ \t]+/, "");
    if (!line.startsWith(NEEDS_INPUT_MARKER))
        return null;
    const rest = line.slice(NEEDS_INPUT_MARKER.length);
    if (!/^[ \t]/.test(rest))
        return null; // must be followed by whitespace, then JSON
    const json = rest.trim();
    if (!json.startsWith("{") || !json.endsWith("}"))
        return null;
    if (utf8ByteLength(json) > NEEDS_INPUT_MAX_JSON_BYTES)
        return null;
    let parsed;
    try {
        parsed = JSON.parse(json);
    }
    catch {
        return null;
    }
    // An agent-emitted marker never sets the runner-only kind / prNumber.
    return isNeedsInputRequest(parsed) ? stripRunnerOnlyFields(normalize(parsed)) : null;
}
/**
 * Render a request as plain text (job turns, PR bodies, emails). Sections
 * that are absent are omitted. Ends without a trailing newline.
 */
export function formatNeedsInputText(req) {
    const out = [req.question.trim()];
    if (req.why?.trim())
        out.push("", `Why: ${req.why.trim()}`);
    if (req.steps && req.steps.length > 0) {
        out.push("", "Steps:");
        req.steps.forEach((s, i) => out.push(`  ${String(i + 1)}. ${s}`));
    }
    if (req.options && req.options.length > 0) {
        out.push("", "Options (quickest first):");
        req.options.forEach((o, i) => {
            out.push(`  Option ${String(i + 1)}: ${o.label}${o.onMachine ? " (runs on your machine)" : ""}`);
            o.steps.forEach((s, j) => out.push(`    ${String(j + 1)}. ${s}`));
        });
    }
    if (req.ownerItems && req.ownerItems.length > 0) {
        out.push("", "Also waiting on you:");
        for (const item of req.ownerItems)
            out.push(`  - ${item.title}: ${item.how}`);
    }
    if (req.afterAnswer?.trim()) {
        out.push("", `After you answer: ${req.afterAnswer.trim()}`);
    }
    out.push("", "Never paste secret values in your reply. Store them as GitHub environment secrets or in your cloud secret manager and reply with where you put them.");
    return out.join("\n");
}
const SECRET_PATTERNS = [
    { re: /-----BEGIN [A-Z0-9 ]*PRIVATE KEY-----/, reason: "a private key block (-----BEGIN ... PRIVATE KEY-----)" },
    { re: /(?<![A-Za-z0-9])sk-ant-[A-Za-z0-9_-]{16,}/, reason: "an Anthropic API key (sk-ant-)" },
    { re: /(?<![A-Za-z0-9])sk-[A-Za-z0-9_-]{16,}/, reason: "an API key starting with sk-" },
    { re: /(?<![A-Za-z0-9])[sr]k_(?:live|test)_[A-Za-z0-9]{16,}/, reason: "a Stripe secret key (sk_live_/sk_test_)" },
    { re: /(?<![A-Za-z0-9])github_pat_[A-Za-z0-9_]{20,}/, reason: "a GitHub fine-grained token (github_pat_)" },
    { re: /(?<![A-Za-z0-9])ghp_[A-Za-z0-9]{20,}/, reason: "a GitHub personal access token (ghp_)" },
    { re: /(?<![A-Za-z0-9])gho_[A-Za-z0-9]{20,}/, reason: "a GitHub OAuth token (gho_)" },
    { re: /(?<![A-Za-z0-9])gh[sur]_[A-Za-z0-9]{20,}/, reason: "a GitHub app or refresh token (ghs_/ghu_/ghr_)" },
    { re: /(?<![A-Za-z0-9])glpat-[A-Za-z0-9_-]{20,}/, reason: "a GitLab personal access token (glpat-)" },
    { re: /(?<![A-Za-z0-9])npm_[A-Za-z0-9]{30,}/, reason: "an npm access token (npm_)" },
    { re: /(?<![A-Za-z0-9])AKIA[0-9A-Z]{16}(?![A-Za-z0-9])/, reason: "an AWS access key id (AKIA...)" },
    {
        re: /aws_secret_access_key["']?\s*[=:]\s*["']?[A-Za-z0-9/+]{40}(?![A-Za-z0-9/+])/i,
        reason: "an AWS secret access key (aws_secret_access_key=...)",
    },
    { re: /(?<![A-Za-z0-9])xox[abp]-[A-Za-z0-9-]{10,}/, reason: "a Slack token (xoxa-/xoxb-/xoxp-)" },
    {
        re: /[a-z][a-z0-9+.-]*:\/\/[^\s/:@]+:[^\s/@]+@[^\s/@]+/i,
        reason: "a URL with an embedded password (scheme://user:password@host)",
    },
];
const HIGH_ENTROPY_MIN_LENGTH = 32;
const HIGH_ENTROPY_THRESHOLD = 4.0;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const URL_RE = /^[a-z][a-z0-9+.-]*:\/\//i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Well-known cloud resource-name roots (GCP, etc.). */
const RESOURCE_PREFIX_RE = /^(?:(?:projects|organizations|folders|billingAccounts|locations|subscriptions)\/|arn:)/i;
/** One segment of a path or resource name. */
const PATH_SEGMENT_RE = /^[a-z0-9._~-]{1,40}$/i;
/** A bare AWS secret access key: exactly 40 base64-ish chars. */
const AWS_SECRET_RE = /^[A-Za-z0-9/+]{40}$/;
/** Shannon entropy in bits per character. */
export function shannonEntropy(text) {
    if (text.length === 0)
        return 0;
    const counts = new Map();
    let len = 0;
    for (const ch of text) {
        counts.set(ch, (counts.get(ch) ?? 0) + 1);
        len++;
    }
    let bits = 0;
    for (const n of counts.values()) {
        const p = n / len;
        bits -= p * Math.log2(p);
    }
    return bits;
}
/**
 * True when a '/'-containing token reads as a path or resource name rather
 * than a secret: a known resource root (projects/..., organizations/...), or
 * two or more short segments of plain name characters.
 */
function looksLikePathOrResource(token) {
    if (RESOURCE_PREFIX_RE.test(token))
        return true;
    const segments = token.split("/").filter((s) => s.length > 0);
    return segments.length >= 2 && segments.every((s) => PATH_SEGMENT_RE.test(s));
}
/** A bare 40-char AWS secret access key (mixed case + digit, high entropy). */
function isAwsSecretToken(token) {
    if (!AWS_SECRET_RE.test(token))
        return false;
    if (RESOURCE_PREFIX_RE.test(token))
        return false;
    if (!/[A-Z]/.test(token) || !/[a-z]/.test(token) || !/[0-9]/.test(token))
        return false;
    return shannonEntropy(token) > HIGH_ENTROPY_THRESHOLD;
}
function isHighEntropyToken(token) {
    if (token.length < HIGH_ENTROPY_MIN_LENGTH)
        return false;
    if (token.includes("/") && looksLikePathOrResource(token))
        return false;
    if (UUID_RE.test(token))
        return false;
    // Real keys mix letters and digits; long identifiers and words usually don't.
    if (!/[0-9]/.test(token) || !/[A-Za-z]/.test(token))
        return false;
    return shannonEntropy(token) > HIGH_ENTROPY_THRESHOLD;
}
/**
 * Conservative check for secret values in free text (e.g. an owner's reply).
 *
 * Flags well-known key prefixes (sk-, sk-ant-, sk_live_, ghp_, gho_, ghs_,
 * ghu_, ghr_, github_pat_, glpat-, npm_, AKIA..., xox[abp]-), private key
 * blocks, AWS secret access keys (40 chars of [A-Za-z0-9/+], always after
 * aws_secret_access_key=), URLs carrying a password (scheme://user:pass@host),
 * and long (32+ chars) letter+digit tokens whose Shannon entropy exceeds 4.0
 * bits/char. A token containing '/' is skipped only when it reads as a path or
 * resource name (projects/..., organizations/..., or 2+ short plain segments).
 * Does NOT flag GCP resource names, URLs, emails, UUIDs, project ids or
 * ordinary sentences.
 */
export function looksLikeSecret(text) {
    if (typeof text !== "string" || text.trim() === "")
        return { secret: false };
    for (const { re, reason } of SECRET_PATTERNS) {
        if (re.test(text))
            return { secret: true, reason: `Looks like ${reason}.` };
    }
    for (const raw of text.split(/[\s"'`,;()<>[\]{}|]+/)) {
        const token = raw.replace(/^[.!?*]+|[.!?*]+$/g, "");
        if (token.length < HIGH_ENTROPY_MIN_LENGTH)
            continue;
        if (URL_RE.test(token) || EMAIL_RE.test(token))
            continue;
        if (isAwsSecretToken(token)) {
            return { secret: true, reason: "Looks like an AWS secret access key (40 chars)." };
        }
        // KEY=value / key: value — check each side on its own.
        for (const part of token.split(/[=:]+/)) {
            if (isHighEntropyToken(part)) {
                return {
                    secret: true,
                    reason: `Looks like a high-entropy token (${String(part.length)} chars starting "${part.slice(0, 4)}...").`,
                };
            }
        }
    }
    return { secret: false };
}
//# sourceMappingURL=needs-input.js.map